<template>
  <div
    class="credit-card"
  >
    <div class="billing-address-card__row-two-col">
      <h2 class="billing-address-card__title">Billing Address</h2>
      <CloseSVG
        v-if="editingMode"
        class="edit-button"
        aria-label="Cancel Editing"
        @click="$emit('cancel')"
      />
    </div>

    <div
      v-if="enableGooglePlaceAutocomplete"
      class="google-search"
    >
      <span class="google-search__label">Start typing address</span>
      <div
        class="stripe-card__row stripe-card__row-two-col"
      >
        <gmp-place-autocomplete
          :id="elementId"
        ></gmp-place-autocomplete>
      </div>
    </div>

    <div
      class="stripe-card__row stripe-card__row-two-col"
      :class="{ 'input-error': addressLine1Error }"
    >
      <div class="stripe-card__number-row">
        <input
          id="billing-address"
          class="cardknox-ach-fields"
          placeholder="Address Line 1"
          :value="addressLine1"
          @input="updateValue('address_line_1', $event.target.value)"
        />
      </div>
    </div>
    <div
      class="stripe-card__row stripe-card__row-two-col"
      :class="{ 'input-error': addressLine2Error }"
    >
      <div class="stripe-card__number-row">
        <input
          class="cardknox-ach-fields"
          placeholder="Address Line 2"
          :value="addressLine2"
          @input="updateValue('address_line_2', $event.target.value)"
        />
      </div>
    </div>
    <div
      class="stripe-card__row stripe-card__row-two-col"
      :class="{ 'input-error': cityError }"
    >
      <div class="stripe-card__number-row">
        <input
          class="cardknox-ach-fields"
          placeholder="City"
          :value="city"
          @input="updateValue('city', $event.target.value)"
        />
      </div>
    </div>
    <div
      class="stripe-card__row stripe-card__row-two-col"
      :class="{ 'input-error': stateError }"
    >
      <div class="stripe-card__number-row">
        <input
          class="cardknox-ach-fields"
          placeholder="State"
          :value="state"
          @input="updateValue('state', $event.target.value)"
        />
      </div>
    </div>
    <div
      class="stripe-card__row stripe-card__row-two-col"
      :class="{ 'input-error': zipError }"
    >
      <div class="stripe-card__number-row">
        <input
          class="cardknox-ach-fields"
          placeholder="Zip"
          :value="zip"
          @input="updateValue('zip', $event.target.value)"
        />
      </div>
    </div>
    <div
      class="stripe-card__row stripe-card__row-two-col"
      :class="{ 'input-error': countryError }"
    >
      <div class="stripe-card__number-row">
        <input
          class="cardknox-ach-fields"
          placeholder="Country"
          :value="country"
          @input="updateValue('country', $event.target.value)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CloseSVG from '@/assets/images/icons/close.svg'
import { mapGetters } from 'vuex'
import googlePlaceAutocompleteMixin from '@core/mixins/googlePlaceAutocompleteMixin.js'
import uniqueId from 'lodash/uniqueId'

export default {
  components: { CloseSVG },
  mixins: [googlePlaceAutocompleteMixin],
  computed: {
    ...mapGetters({
      authSettings: 'authSettings/getAuthSettings',
    }),
    enableGooglePlaceAutocomplete() {
      return this.authSettings.google_services?.places_autocomplete ?? false
    },
    billingAddress() {
      return this.$store.getters['cards/getBillingAddressData']
    },
    addressLine1() {
      return this.$store.getters['formValidation/getBillingAddressField']('address_line_1')
    },
    addressLine1Error() {
      return this.$store.getters['formValidation/getBillingAddressError']('address_line_1')
    },
    addressLine2() {
      return this.$store.getters['formValidation/getBillingAddressField']('address_line_2')
    },
    addressLine2Error() {
      return this.$store.getters['formValidation/getBillingAddressError']('address_line_2')
    },
    city() {
      return this.$store.getters['formValidation/getBillingAddressField']('city')
    },
    cityError() {
      return this.$store.getters['formValidation/getBillingAddressError']('city')
    },
    state() {
      return this.$store.getters['formValidation/getBillingAddressField']('state')
    },
    stateError() {
      return this.$store.getters['formValidation/getBillingAddressError']('state')
    },
    zip() {
      return this.$store.getters['formValidation/getBillingAddressField']('zip')
    },
    zipError() {
      return this.$store.getters['formValidation/getBillingAddressError']('zip')
    },
    country() {
      return this.$store.getters['formValidation/getBillingAddressField']('country')
    },
    countryError() {
      return this.$store.getters['formValidation/getBillingAddressError']('country')
    },
  },
  props: {
    editingMode: {
      type: Boolean,
      required: true,
    },
    creatingMode: {
      type: Boolean,
      required: true,
    },
  },
  watch: {
    creatingMode: {
      handler() {
        this.setFormData()
      },
      immediate: true,
    },
  },
  data() {
    return {
      elementId: uniqueId(),
    }
  },
  mounted() {
    if (this.enableGooglePlaceAutocomplete) {
      this.loadGoogleMapsAPI()
        .then(() => {
          this.$nextTick(() => {
            this.attachPlaceSelectListener(this.handlePlaceSelect, this.elementId)
          })
        })
        .catch(error => {
          console.error('Error initial Google Maps:', error)
        })
    }
  },
  beforeDestroy() {
    const autocompleteElement = document.getElementById(this.elementId)
    if (autocompleteElement) {
      autocompleteElement.removeEventListener('gmp-select', this.handlePlaceSelect)
    }
  },
  methods: {
    handlePlaceSelect(event) {
      const placePrediction = event.placePrediction

      if (placePrediction) {
        const place = placePrediction.toPlace()

        this.fetchPlaceDetails(place).then(() => {
          this.setPlaceDetails()
        })
      }
    },
    async setPlaceDetails() {
      Object.entries(this.googleAddressDetails).forEach(([field, value]) => {
        this.$store.commit('formValidation/setBillingAddressFormData', { field, value })
      })
    },
    setFormData() {
      if (this.billingAddress?.id) {
        Object.entries(this.billingAddress).forEach(([field, value]) => {
          this.$store.commit('formValidation/setBillingAddressFormData', { field, value })
        })
      } else {
        this.$store.commit('formValidation/resetBillingAddressForm')
      }
    },
    updateValue(field, val) {
      this.$store.commit('formValidation/setBillingAddressFormData', {
        field: field,
        value: val,
      })
    }
  }
}
</script>

<style lang="scss">
iframe {
  border: 0 solid transparent;
  height: 45px;
  padding: 0px;
  margin-bottom: 5px;
}

.input-error{
  border: 1px solid red;
}

gmp-place-autocomplete {
  background-color: #EDEDED;
  color-scheme: light;
  border: none !important;
  padding: 0;
  font-family: 'Calibri', sans-serif;
  font-size: 16px;
  --gmpx-input-color: #333333;
  --gmpx-input-font-size: 16px;
}
</style>
